/**
 * UsageRing — SVG ring progress component for usage metrics.
 * Transitions through green → amber → red as usage approaches the limit.
 */

import React from "react";
import { cn } from "../../lib/utils";

interface UsageRingProps {
  current: number;
  limit: number;
  label: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

function getRingColor(pct: number): string {
  if (pct >= 100) return "#ef4444"; // red-500
  if (pct >= 80) return "#f59e0b";  // amber-500
  return "#6366f1";                  // emerald-500 (brand-ish)
}

function getLabelColor(pct: number): string {
  if (pct >= 100) return "text-red-500";
  if (pct >= 80) return "text-amber-500";
  return "text-foreground";
}

export default function UsageRing({
  current,
  limit,
  label,
  size = 100,
  strokeWidth = 8,
  className,
}: UsageRingProps) {
  const isUnlimited = limit === -1;
  const pct = isUnlimited ? 0 : Math.min(100, (current / Math.max(limit, 1)) * 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const color = getRingColor(pct);
  const center = size / 2;

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
        >
          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-secondary"
          />
          {/* Progress */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={isUnlimited ? 0 : strokeDashoffset}
            style={{ transition: "stroke-dashoffset 0.6s ease, stroke 0.4s ease" }}
          />
        </svg>

        {/* Centre text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("font-bold leading-none text-base", getLabelColor(pct))}>
            {current}
          </span>
          <span className="text-[10px] text-muted-foreground leading-none mt-0.5">
            {isUnlimited ? "∞" : `/ ${limit}`}
          </span>
        </div>
      </div>

      <span className="text-xs font-medium text-muted-foreground text-center leading-tight">
        {label}
      </span>

      {!isUnlimited && (
        <span
          className={cn(
            "text-[10px] font-semibold",
            pct >= 100
              ? "text-red-500"
              : pct >= 80
              ? "text-amber-500"
              : "text-muted-foreground"
          )}
        >
          {pct >= 100 ? "Limit reached" : pct >= 80 ? `${Math.round(pct)}% used` : `${Math.round(pct)}% used`}
        </span>
      )}
    </div>
  );
}
